'use client'

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { Drawer } from '@/components/atoms'

type DrawerContent = {
  content: ReactNode
  title?: string
  mask?: boolean
}

type DrawerContextProps = {
  showDrawer: ({ content, title, mask }: DrawerContent) => void
  hideDrawer: () => void
  drawerIsOpen: boolean
}

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined)

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [drawerContent, setDrawerContent] = useState<DrawerContent>({
    content: null,
    title: '',
    mask: false,
  })
  const [drawerIsOpen, setDrawerIsOpen] = useState(false)

  const showDrawer = useCallback((props: DrawerContent) => {
    setDrawerContent(props)
    setDrawerIsOpen(true)
  }, [])

  const hideDrawer = useCallback(() => {
    setDrawerIsOpen(false)
    setDrawerContent({
      content: null,
    })
  }, [])

  const contextValue = useMemo(
    () => ({ showDrawer, hideDrawer, drawerIsOpen }),
    [showDrawer, hideDrawer, drawerIsOpen],
  )

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}
      <Drawer
        open={drawerIsOpen}
        setOpen={setDrawerIsOpen}
        title={drawerContent.title}
        mask={drawerContent.mask}
      >
        {drawerContent.content}
      </Drawer>
    </DrawerContext.Provider>
  )
}

export const useDrawer = () => {
  const context = useContext(DrawerContext)
  if (context === undefined) {
    throw new Error('useDrawer must be used within a DrawerProvider')
  }
  return context
}
